import React, { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Tabs,
  Banner,
  TextContainer,
  Text,
  BlockStack,
} from '@shopify/polaris';
import { InteractiveMessageBuilder } from '../components/InteractiveMessageBuilder';
import { CatalogManager } from '../components/CatalogManager';
import { FlowBuilder } from '../components/FlowBuilder';
import { PaymentsDashboard } from '../components/PaymentsDashboard';

interface InteractiveMessagesPageProps {
  storeId: string;
}

export const InteractiveMessagesPage: React.FC<InteractiveMessagesPageProps> = ({ storeId }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSendMessage = async (messageData: any) => {
    try {
      // Determine which endpoint to use based on message structure
      let endpoint = '/api/whatsapp/interactive/';

      if (messageData.buttons) {
        if (messageData.buttons[0]?.type === 'url' || messageData.buttons[0]?.type === 'phone_number') {
          endpoint += 'cta';
        } else {
          endpoint += 'buttons';
        }
      } else if (messageData.sections) {
        endpoint += 'list';
      } else if (messageData.productRetailerId) {
        endpoint += 'product';
      } else if (messageData.catalogId && messageData.sections) {
        endpoint += 'product-list';
      } else if (messageData.flowId) {
        endpoint += 'flow';
      } else {
        throw new Error('Invalid message type');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messageData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send message');
      }

      const data = await response.json();

      // Show success notification
      alert('Message sent successfully!');

      return data;
    } catch (error: any) {
      console.error('Failed to send message:', error);
      throw error;
    }
  };

  const tabs = [
    {
      id: 'send-message',
      content: 'Send Interactive Message',
      panelID: 'send-message-panel',
    },
    {
      id: 'catalogs',
      content: 'Product Catalogs',
      panelID: 'catalogs-panel',
    },
    {
      id: 'flows',
      content: 'WhatsApp Flows',
      panelID: 'flows-panel',
    },
    {
      id: 'payments',
      content: 'Payments',
      panelID: 'payments-panel',
    },
  ];

  return (
    <Page
      title="WhatsApp Interactive Features"
      subtitle="Send interactive messages, manage catalogs, flows, and payments"
    >
      <Layout>
        <Layout.Section>
          <Banner tone="info">
            <TextContainer>
              <p>
                WhatsApp interactive features allow you to create rich, engaging experiences
                for your customers directly in WhatsApp.
              </p>
              <div>
                <p>
                  <strong>Interactive Buttons:</strong> Quick reply buttons for easy customer responses
                </p>
                <p>
                  <strong>List Messages:</strong> Dropdown menus with multiple options
                </p>
                <p>
                  <strong>Product Catalogs:</strong> Showcase products with images and prices
                </p>
                <p>
                  <strong>Flows:</strong> Multi-step forms for data collection
                </p>
                <p>
                  <strong>Payments:</strong> Accept payments directly in WhatsApp (India only)
                </p>
              </div>
            </TextContainer>
          </Banner>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
              {selectedTab === 0 && (
                <BlockStack gap="500">
                  <InteractiveMessageBuilder
                    onSend={handleSendMessage}
                  />
                </BlockStack>
              )}

              {selectedTab === 1 && (
                <BlockStack gap="500">
                  <CatalogManager
                    storeId={storeId}
                    onError={setErrorMessage}
                  />
                </BlockStack>
              )}

              {selectedTab === 2 && (
                <BlockStack gap="500">
                  <FlowBuilder
                    storeId={storeId}
                    onError={setErrorMessage}
                  />
                </BlockStack>
              )}

              {selectedTab === 3 && (
                <BlockStack gap="500">
                  <PaymentsDashboard
                    storeId={storeId}
                    onError={setErrorMessage}
                  />
                </BlockStack>
              )}
            </Tabs>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="500">
              <Text variant="headingMd" as="h3">Getting Started</Text>
              <TextContainer>
                <p><strong>1. Set Up Product Catalog</strong></p>
                <p>Go to the "Product Catalogs" tab to sync your store products to WhatsApp.</p>

                <p><strong>2. Create WhatsApp Flows</strong></p>
                <p>Use the "WhatsApp Flows" tab to create interactive forms in Meta Business Manager, then add them here.</p>

                <p><strong>3. Send Interactive Messages</strong></p>
                <p>Use the message builder to send buttons, lists, products, or flows to customers.</p>

                <p><strong>4. Monitor Payments</strong></p>
                <p>Track payment transactions in the "Payments" tab.</p>
              </TextContainer>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="500">
              <Text variant="headingMd" as="h3">Best Practices</Text>
              <TextContainer>
                <ul>
                  <li>Use buttons for simple yes/no or quick choices (max 3)</li>
                  <li>Use lists for 4+ options or categorized choices</li>
                  <li>Keep button text under 20 characters</li>
                  <li>Use clear, action-oriented language</li>
                  <li>Test messages before sending to customers</li>
                  <li>Monitor analytics to optimize performance</li>
                </ul>
              </TextContainer>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="500">
              <Text variant="headingMd" as="h3">Requirements</Text>
              <TextContainer>
                <p><strong>WhatsApp Business API Access</strong></p>
                <p>You need a WhatsApp Business Account with API access to use these features.</p>

                <p><strong>Catalog Features</strong></p>
                <p>Requires Facebook Catalog Manager setup and catalog ID.</p>

                <p><strong>Flow Features</strong></p>
                <p>Create flows in Meta Business Manager's Flow Builder first.</p>

                <p><strong>Payment Features</strong></p>
                <p>Currently only available in India with approved business account and UPI integration.</p>
              </TextContainer>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="500">
              <Text variant="headingMd" as="h3">Need Help?</Text>
              <TextContainer>
                <p>
                  Check the{' '}
                  <a
                    href="https://developers.facebook.com/docs/whatsapp/cloud-api/guides/send-messages"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    WhatsApp Cloud API Documentation
                  </a>{' '}
                  for detailed information about interactive messages.
                </p>
              </TextContainer>
            </BlockStack>
          </Card>
        </Layout.Section>

        {errorMessage && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setErrorMessage('')}>
              <p>{errorMessage}</p>
            </Banner>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
};
